<:Window on:resize='resize()' />

<Slide :slide :i>

    {{#each slide.text_list as text_item, n}}
    <div class="slide__text slide__text--{{ n + 1 }} slide__text--interactive step">
        {{#if show_visuals}}

            {{#if text_item.type == 'Text'}}
                <p class="slide__text-inner" transition:fade>{{ text_item.text }}</p>
            {{elseif text_item.type == 'Video' }}
                <Modal slideIndex={{i}} modalIndex={{n}} modal={{text_item}} />
            {{elseif text_item.type == 'Aside' }}
                <Modal slideIndex={{i}} modalIndex={{n}} modal={{$science_asides[text_item.slug]}} />
            {{else}}
                <p class="slide__text-inner" transition:fade>{{ text_item }}</p> 
            {{/if}}

        {{/if}}
    </div>
    {{/each}}

    <div class="media-outer interactive">
        <video muted playsinline ref:video on:ended="onEnd(event)" class="{{ show_loop ? 'hide' : 'show' }}">
            <source src="{{ video('co2_intro.webm') }}" type="video/webm">
            <source src="{{ video('co2_intro.mp4') }}" type="video/mp4">
        </video>

        <video muted playsinline loop ref:loop class="{{ show_loop ? 'show' : 'hide' }}">
            <source src="{{ video('co2_loop.webm') }}" type="video/webm">
            <source src="{{ video('co2_loop.mp4') }}" type="video/mp4">
        </video>

        <audio ref:audio on:ended='audioEnd()'>
            <source src="{{ media('audio/vo/Ju_Audio_AtmosAngry.mp3') }}" type="audio/mp3">
            {{#if slide.vtt}}
            <track ref:track label="English captions" src="{{ slide.vtt }}" kind="metadata" srclang="en" default>
            {{/if}}
        </audio>
    </div>

    <div class="i-co2">
        <div class="i-co2__outer">
            <div class="i-co2__inner">
                <svg class="i-co2__chart" ref:chart>
                    
                    {{#if rows.length && show_chart}}
                    <g class="x axis" transform="translate(0, {{ yCO2(0) }})">
                        <line x0="0" x1="{{ width }}" y0="0" y1="0" transition:fade></line>
                    </g>

                    <g class="co2 bars">
                    {{#each visible as row, i}}
                        {{#if row.CO2 >= 0}}
                        <rect transition:rise x="{{ x(row.century) }}" width="{{ x.bandwidth() / 2 }}"
                            y="{{ yCO2(row.CO2) }}" height="{{ yCO2(0) - yCO2(row.CO2) }}"></rect>
                        {{else}}
                        <rect transition:rise x="{{ x(row.century) }}" width="{{ x.bandwidth() / 2 }}"
                            y="{{ yCO2(0) }}" height="{{ yCO2(row.CO2) - yCO2(0) }}"></rect>
                        {{/if}}
                    {{/each}}
                    </g>

                    <g class="sealevel bars">
                    {{#each visible as row, i}}
                        {{#if row.sealevel_mm >= 0}}
                        <rect x="{{ x(row.century) + x.bandwidth() / 2 }}" width="{{ x.bandwidth() / 2 }}"
                            y="{{ ySL(row.sealevel_mm) }}" height="{{ ySL(0) - ySL(row.sealevel_mm) }}"></rect>
                        {{else}}
                        <rect x="{{ x(row.century) + x.bandwidth() / 2 }}" width="{{ x.bandwidth() / 2 }}"
                            y="{{ ySL(0) }}" height="{{ ySL(row.sealevel_mm) - ySL(0) }}"></rect>
                        {{/if}}
                    {{/each}}
                    </g>
                    {{/if}}
                </svg>
                {{#if show_chart}}
                <div class="i-co2__chart-labels">
                    <p class="i-co2__chart-label label-1">1st</p>
                    <p class="i-co2__chart-label label-10">10th</p>

                    {{#if $bullet[0] === i && $bullet[1] < 2}}
                    <p class="i-co2__chart-label label-19" transition:fade>19th</p>
                    {{else}}
                    <p class="i-co2__chart-label label-20" transition:fade>20th</p>
                    {{/if}}
                </div>
                {{/if}}
            </div>
        </div>
    </div>

    {{#if show_chart}}
    <div class="i-co2__legend" transition:fade>
        <p>Change in <span class="co2">carbon dioxide</span> 
            {{#if show_legend}}and <span class="sealevel">sea level</span>{{/if}}
            by century
        </p>
    </div>
    {{/if}}

    <Captions ref:display :cues />

</Slide>

<script>
import Slide from '../Slide.html'
import Captions from '../video/Captions.html'
import Modal from '../Modal.html'
import { media, video, noop, syncCues } from '../helpers'

import { extent } from 'd3-array'
import { csv } from 'd3-fetch'
import { scaleLinear, scaleBand } from 'd3-scale'
import { timeout } from 'd3-timer'

import { tween } from 'svelte-extras'
import { fade } from 'svelte-transitions'


function getVisibleRows(rows=[], step=-1) {

    if (step < 0) {
        return []
    }

    if (step === 0) {
        return rows.filter(r => r.century < 20).map(r => {
            return {
                century: r.century,
                CO2: r.CO2,
                sealevel_mm: 0
            }
        }).concat({
            century: 20,
            CO2: 0,
            sealevel_mm: 0
        });
    }

    if (step === 1) {
        return rows.filter(r => r.century < 20)
            .concat({
                century: 20,
                CO2: 0,
                sealevel_mm: 0
            });
    }

    if (step === 2) {
        const copy = [...rows];
        const last = Object.assign({}, copy[copy.length - 1]);

        last.sealevel_mm = 0;
        copy[copy.length - 1] = last;

        return copy;
    }

    return rows;
}

export default {
    components: { Slide, Captions, Modal },

    transitions: {
        fade,

        // slide up from x axis
        rise(node, { delay=0, duration=400 }) {
            const height = +node.getAttribute('height');
            const y = +node.getAttribute('y');

            return {
                delay,
                duration,
                tick: t => {
                    node.setAttribute('height', t * height);
                    node.setAttribute('y', (y + height) - t * height);
                }
            }
        },

        // slide down from x axis
        fall(node, { delay=0, duration=400 }) {
            const height = +node.getAttribute('height');

            return {
                delay,
                duration,
                tick: t => node.setAttribute('height', t * height)
            }
        }
    },

    data() {

        return {
            rows: [],
            visible: [],
            show_visuals: false,
            show_chart: false,
            show_legend: false,
            width: 0,
            height: 0,

            cues: [],
        }
    },

    computed: {

        centuries: rows => rows.map(c => c.century),

        CO2: rows => rows.map(c => c.CO2),

        sealevel: rows => rows.map(c => c.sealevel_mm),

        x: (centuries, width) => {
            return scaleBand()
                .domain(centuries)
                .range([0, width])
            },

        ySL: (height, sealevel) => {
            return scaleLinear()
                .domain([-60, 150])
                .range([height, 0])
                .nice()
        },

        yCO2: (height, sealevel) => {
            return scaleLinear()
                .domain([-30, 75])
                .range([height, 0])
                .nice()
        } 
    },

    oncreate() {
        window.co2 = this;

        this.observers = [];

        csv(window.DATA.co2, d => {
            return {
                century: +d.century,
                CO2: +d.CO2,
                sealevel_mm: +d['sealevel-mm'],
                //sealevel_in: +d['sealevel-in'], // maybe drop this later
            }
        }).then(rows => {
            this.set({ rows, visible: getVisibleRows(rows, 0) });
        });

        this.observers.push(this.store.observe('muted', muted => {
            this.refs.audio.muted = muted;
        }));

        this.observers.push(this.store.observe('bullet', current => {
            const [slide, bullet] = (current || []);
            
            // only run here
            if (slide !== this.get('i')) return;
            const show_legend = bullet >= 1;
            const rows = this.get('rows') || [];
            this.tween('visible', getVisibleRows(rows, bullet));
            this.set({ show_legend });
        }));

        this.observers.push(this.store.observe('current_slide', (current, prev) => {

            const { i, show_visuals } = this.get();

            if (current === i && !show_visuals) {
                this.play();
            }

            if (prev === i) {
                this.pause();
            }

            if (current === i - 1) {
                this.set({ show_visuals: false });
            }
        }));

        syncCues(this.refs.track, this.refs.display);

        this.resize();
    },


    ondestroy() {
        for (let i = this.observers.length - 1; i >= 0; i--) {
            this.observers[i].cancel();
        }
    },

    methods: {

        tween, 

        resize() {
            const {width, height} = this.refs.chart.getBoundingClientRect();
            this.set({ width, height });
        },

        play() {
            const {video, audio} = this.refs;
            this.set({ 
                show_loop: false, 
                show_visuals: false, 
                show_chart: false, 
                show_legend: false 
            });
            
            video && video.play()
                .catch(noop)
                .then(() => {
                    this.store.deck.freeze();

                    timeout(() => {
                        this.set({ show_chart: true });
                    }, 8000 - video.currentTime);
                });

            audio && audio.play().catch(noop);

        },

        pause() {
            const {video, audio} = this.refs;

            this.store.deck.unfreeze();
            video && !video.paused && video.pause();
            audio && !audio.paused && audio.pause();
        },

        onEnd() {
            this.store.deck.unfreeze();
            this.refs.loop.play().catch(noop);
            this.set({ show_loop: true });
        },

        audioEnd() {
            this.set({ show_visuals: true });
            this.store.deck.unfreeze();
        }
    },

    helpers: {
        media,
        video,
    }

}
</script>